import {Button, Col, Collapse, Modal, Row, Spin} from "antd";
import React, {useMemo} from "react";
import {useGetValuesByIssueIdQuery} from "@mwServices/Issue";
import {InfoIcon, KeyIcon} from "@components/icons/Icons";
import {showInfoModal} from "@lib/showModal";
import {Grades} from "@lib/constants";
import {formatMoney} from "@lib/format";
import {DownOutlined, RightOutlined} from "@ant-design/icons";
import useSubscriptionType from "@hooks/useSubscriptionType";
import {KeyIssueAccordion} from "@components/key-info-accordion";

interface ComicPricesModalProps {
    isModalOpen: boolean;
    issueId: number;
    onCancel: () => void;
    info?: {
        key_info?: string;
        slug?: string;
    };
}

const ComicPricesModal = ({ isModalOpen, issueId, onCancel, info }: ComicPricesModalProps) => {

    const subscriptionType = useSubscriptionType();
    const showPrices = useMemo(() => (
            subscriptionType.isStandard ||
            subscriptionType.isTrial ||
            subscriptionType.isUnlimited),
        [subscriptionType]
    );

    const valuesQuery = useGetValuesByIssueIdQuery({issueId});

    const valuesData = useMemo(() => {
        if (valuesQuery.data) {
            return valuesQuery.data.values;
        }
        return null;
    }, [valuesQuery.data]);

    const getGradeValue = (grade, isSlab=false) => {
        if(valuesData){
            if((isSlab && valuesData.slabbed) || (!isSlab && valuesData.raw)){
                const data = isSlab ? valuesData.slabbed.data : valuesData.raw.data;
                const gradeData = data && data.find((data) => data.grade === grade);
                if(gradeData) return (
                    <span className={gradeData.stale === 1 ? ' stale' : ''}>
                        {formatMoney(gradeData.value) + (gradeData.stale === 1 ? '*' : '')}
                    </span>);
            }
        }
        return "--";
    }

    const [showKeyInfo, setShowKeyInfo] = React.useState(false);

    const gotoSalesData = (isRaw=false) => window.location.href = '/issue-sales/?issue_id=' + issueId + (isRaw && '&tab=raw');

    const onInfoClick = () => {
        showInfoModal({
            title: "Comic Prices",
            content: (
                <>
                    <p>COVRPRICE helps you estimate a comic's Fair Market Value (FMV) using real sales data for both professionally graded ("slabbed") and ungraded comics ("raw").</p>
                    <p>For each comic, we analyze past sales and identify the most common individual grade (slabbed) or grade range (raw) based on those sales. This "most common" category reflects the typical condition most buyers encounter and is presented as the Most Common Condition (MCC across our website).</p>
                    <p>Because raw comic values are based on individual subjective estimates from sellers and buyers, we group raw comics into grade ranges (or buckets) to help reduce the variability. This allows us to provide a more realistic FMV for raw comics. For example, a Raw NM (Near Mint) FMV will include raw comics with stated grades of 9.4, 9.2, and 9.0.</p>
                    <p>COVRPRICE calculates our FMV using a weighted moving average. This means we average recent sales for that specific grade/range, giving more weight to the most recent transactions. This approach helps capture current market trends, like a sudden rise or fall in value.</p>
                    <p><b>Please Note:</b></p>
                    <p>While COVRPRICE works hard to present accurate data, there's always a chance for errors.</p>
                    <p>Our FMVs are a valuable starting point, but not a guaranteed price. The actual value can be influenced by factors like the comic's specific condition, rarity, and collector demand.</p>
                    <p>Always use your judgment and consider all factors before buying or selling a comic. If a deal seems unbelievably good, it probably is.</p>
                </>
            ),
        });
    }

    return (
        <Modal title={null} open={isModalOpen} onCancel={onCancel} centered={true} destroyOnClose={true} footer={null} className="comic-prices-modal">

            {info && info.key_info && (
                <div  className={"mar-top-25"}>
                    <KeyIssueAccordion keyInfo={info.key_info} defaultExpanded={false} />
                </div>
            )}

            {showPrices ? (
                <Spin size="large" spinning={valuesQuery.isFetching || valuesQuery.isLoading}>
                    <Row gutter={[10,10]} style={{ marginTop: 20}} >
                        <Col span={12} >
                            <div className="grade-header raw-header">
                                <Button className="info-btn" onClick={onInfoClick} type="link" icon={<InfoIcon />}/>
                                <b>RAW</b>&nbsp;RANGES
                            </div>

                            <div>
                                {Grades.raw.map((rawBucket) => (
                                    <Row
                                        key={'raw-'+rawBucket.short_desc}
                                        className="grade-bucket raw-bucket"
                                        style={{height: (30 * (Grades.slabbed.filter((grade) => (grade.high <= rawBucket.high) && (grade.low >= rawBucket.low)).length)),}}
                                        onClick={() => gotoSalesData(true)}
                                    >
                                        <Col span={8} className="text-column flex-align-center bucket-header">
                                            {rawBucket.short_desc}
                                        </Col>
                                        <Col span={16} className="text-column flex-align-center bucket-value" >
                                            {getGradeValue(rawBucket.short_desc, false)}
                                        </Col>
                                    </Row>
                                ))}
                            </div>
                        </Col>

                        <Col span={12}>
                            <div className="grade-header slabbed-header">
                                <Button className="info-btn" onClick={onInfoClick} type="link" icon={<InfoIcon />}/>
                                <b>SLABBED</b>&nbsp;SALES
                            </div>

                            <div>
                                {Grades.raw.map((rawBucket) => (
                                    <div
                                        key={'slabbed-bucket-'+rawBucket.short_desc}
                                        className="grade-bucket slabbed-bucket"
                                        onClick={() => gotoSalesData(false)}
                                    >
                                        {Grades.slabbed
                                            .filter((grade) => (grade.high <= rawBucket.high) && (grade.low >= rawBucket.low))
                                            .map((grade, i, arr) => (
                                                <Row key={'slabbed-' + grade.short_desc} className="price-row">
                                                    <Col span={8} className="text-column flex-align-center bucket-header">
                                                        {grade.short_desc}
                                                        {(i+1 !== arr.length) && (
                                                            <div className="bottom-line" />
                                                        )}
                                                    </Col>
                                                    <Col span={16} className="text-column flex-align-center bucket-value" >
                                                        {getGradeValue(grade.short_desc, true)}
                                                        {(i+1 !== arr.length) && (
                                                            <div className="bottom-line" />
                                                        )}
                                                    </Col>
                                                </Row>
                                            ))
                                        }
                                    </div>
                                ))}
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={24} >
                            *indicates sales older than 90 days.
                        </Col>
                        <Col span={24} >
                            <div className="bottom-line-black" />
                        </Col>
                        {info && info.slug && (
                            <Col span={24} className={"flex-align-center"} >
                                <Button size="large" className={"greyButton pad-left-20 pad-right-20"} href={`/issue/${info.slug}`} >
                                    See Detail Page <RightOutlined />
                                </Button>
                            </Col>
                        )}
                    </Row>
                </Spin>
            ) : (
                <Row>
                    <Col span={24} className={"flex-align-center flex-vertical-center"} style={{height: 100}}>
                        <p>Please upgrade for this and many other benefits.&nbsp;</p>
                        <p><a className="upgrade-link" href="/plans/unlimited/"><b>Upgrade Now!</b></a></p>
                    </Col>
                    <Col span={24}>
                        <div className="bottom-line-black" />
                    </Col>
                    {info && info.slug && (
                        <Col span={24} className={"flex-align-center"} >
                            <Button size="large" className={"greyButton pad-left-20 pad-right-20"} href={`/issue/${info.slug}`} >
                                See Detail Page <RightOutlined />
                            </Button>
                        </Col>
                    )}
                </Row>
            )}
        </Modal>
    );
};

export default ComicPricesModal;